// VI-368 (resume): local persistence for recorded takes so a jobseeker who
// refreshes, disconnects, or closes the browser mid-question can come back and
// still see / pick / submit the takes they already recorded. Stored in IndexedDB
// (survives browser close) — keyed by applicant + question so a shared device
// never leaks one applicant's takes to another.
//
// VI-702: we persist the recording as an ArrayBuffer + mimeType (NOT a Blob).
// WebKit/Safari has a long-standing bug where a Blob stored directly in IndexedDB
// becomes unreadable after retrieval — which is why, on Safari, a restored take
// errored on playback after refresh while Chrome/Edge/Firefox were fine. Storing
// the raw bytes and reconstructing the Blob on read is portable across browsers.
//
// Used by BOTH the video (useRecording/Interview) and audio (useAudio/AudioInterview)
// flows — the `mediaType` field tells the restore code how to render each take.

const DB_NAME = 'InterviewVideos';
const DB_VERSION = 3;
const STORE = 'takes';
const BY_QUESTION_INDEX = 'byApplicantQuestion';

// Shape persisted in IndexedDB.
interface StoredTakeRecord {
  id: string; // `${applicantGuid}_q${questionIndex}_take${takeIndex}`
  applicantGuid: string;
  questionIndex: number;
  takeIndex: number;
  mediaType: 'video' | 'audio';
  data: ArrayBuffer;   // raw recording bytes (Safari-safe)
  mimeType: string;    // e.g. video/webm;codecs=vp9,opus
  blob?: Blob;         // legacy field from pre-VI-702 records (back-compat on read)
  thumbnail: string;
  duration: number;
  recordedAt: number;
  questionText: string;
  s3MetaData: unknown;
}

// What callers pass to saveTake and get back from getTakesForQuestion — a Blob,
// same as before (the ArrayBuffer conversion is an internal storage detail).
export interface SaveTakeInput {
  applicantGuid: string;
  questionIndex: number;
  takeIndex: number;
  mediaType: 'video' | 'audio';
  blob: Blob;
  thumbnail: string;
  duration: number;
  recordedAt: number;
  questionText: string;
  s3MetaData: unknown;
}

export interface RestoredTake {
  id: string;
  applicantGuid: string;
  questionIndex: number;
  takeIndex: number;
  mediaType: 'video' | 'audio';
  blob: Blob;
  thumbnail: string;
  duration: number;
  recordedAt: number;
  questionText: string;
  s3MetaData: unknown;
}

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = event => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex(BY_QUESTION_INDEX, ['applicantGuid', 'questionIndex'], { unique: false });
      }
    };

    request.onsuccess = event => resolve((event.target as IDBOpenDBRequest).result);
    request.onerror = event => reject((event.target as IDBOpenDBRequest).error);
  });
};

const takeId = (applicantGuid: string, questionIndex: number, takeIndex: number) =>
  `${applicantGuid}_q${questionIndex}_take${takeIndex}`;

// Persist one take. Best-effort: callers should catch — a storage failure
// (quota, private mode) must never break the live recording flow.
export const saveTake = async (take: SaveTakeInput): Promise<void> => {
  const data = await take.blob.arrayBuffer(); // Safari-safe: store bytes, not Blob
  const db = await openDB();
  const record: StoredTakeRecord = {
    id: takeId(take.applicantGuid, take.questionIndex, take.takeIndex),
    applicantGuid: take.applicantGuid,
    questionIndex: take.questionIndex,
    takeIndex: take.takeIndex,
    mediaType: take.mediaType,
    data,
    mimeType: take.blob.type || (take.mediaType === 'audio' ? 'audio/webm' : 'video/webm'),
    thumbnail: take.thumbnail,
    duration: take.duration,
    recordedAt: take.recordedAt,
    questionText: take.questionText,
    s3MetaData: take.s3MetaData,
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE], 'readwrite');
    tx.objectStore(STORE).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

const toBlob = (r: StoredTakeRecord): Blob => {
  if (r.data) return new Blob([r.data], { type: r.mimeType || '' });
  if (r.blob) return r.blob; // legacy pre-VI-702 record
  return new Blob([], { type: r.mimeType || '' });
};

// All takes for one applicant+question, ordered by takeIndex.
export const getTakesForQuestion = async (
  applicantGuid: string,
  questionIndex: number,
): Promise<RestoredTake[]> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE], 'readonly');
    const index = tx.objectStore(STORE).index(BY_QUESTION_INDEX);
    const request = index.getAll(IDBKeyRange.only([applicantGuid, questionIndex]));
    request.onsuccess = () => {
      const records = (request.result as StoredTakeRecord[]).sort((a, b) => a.takeIndex - b.takeIndex);
      resolve(records.map(r => ({
        id: r.id,
        applicantGuid: r.applicantGuid,
        questionIndex: r.questionIndex,
        takeIndex: r.takeIndex,
        mediaType: r.mediaType,
        blob: toBlob(r),
        thumbnail: r.thumbnail,
        duration: r.duration,
        recordedAt: r.recordedAt,
        questionText: r.questionText,
        s3MetaData: r.s3MetaData,
      })));
    };
    request.onerror = () => reject(request.error);
  });
};

// Remove all takes for one applicant+question (after a successful submit).
export const deleteTakesForQuestion = async (
  applicantGuid: string,
  questionIndex: number,
): Promise<void> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE], 'readwrite');
    const store = tx.objectStore(STORE);
    const index = store.index(BY_QUESTION_INDEX);
    const request = index.getAllKeys(IDBKeyRange.only([applicantGuid, questionIndex]));
    request.onsuccess = () => {
      (request.result as IDBValidKey[]).forEach(key => store.delete(key));
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

// Drop any take older than maxAgeMs. Run on mount so abandoned interviews don't
// accumulate on the user's disk. Returns the number of records removed.
export const sweepExpiredTakes = async (maxAgeMs: number): Promise<number> => {
  const db = await openDB();
  const cutoff = Date.now() - maxAgeMs;
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE], 'readwrite');
    const store = tx.objectStore(STORE);
    const request = store.getAll();
    let removed = 0;
    request.onsuccess = () => {
      (request.result as StoredTakeRecord[]).forEach(r => {
        if (r.recordedAt < cutoff) {
          store.delete(r.id);
          removed++;
        }
      });
    };
    tx.oncomplete = () => resolve(removed);
    tx.onerror = () => reject(tx.error);
  });
};
